import { getMyPermissionAccess, Role, RoleAssignment } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { logger } from '@adornis/base/logging.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { watchEntireCollection } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Client } from '@nsc/clients/entities/Client.js';
import { NexusPermission } from '@nsc/nexus-permissions/platform-rbac/nexus-permissions.js';
import { NEXUS_ADMIN_ROLE_NAME } from '@nsc/nexus-permissions/platform-rbac/platform-admin-permissions.js';
import { debounceTime, from, switchMap } from 'rxjs';
import { getClientIdsFromScope } from '../features/permissions/scope-helpers.js';

async function loadAccessibleClients(): Promise<Client[]> {
  const info = await getMyPermissionAccess(NexusPermission.ClientView)({
    allowedContexts: 1,
    hasAccess: 1,
    isGlobal: 1,
  });
  const obj = info.toObject?.() ?? {
    hasAccess: info.hasAccess,
    isGlobal: info.isGlobal,
    allowedContexts: info.allowedContexts,
  };

  if (!obj.hasAccess) return [];

  const col = await getRawCollection<EntityData<Client>>(Client._collectionName);
  const ctx = context as { serverContext?: boolean; userID?: string };
  const userId = ctx.serverContext ? undefined : ctx.userID;

  if (ctx.serverContext) return col.find<Client>({}).sort({ name: 1 }).toArray();

  if (!userId) return [];

  const assignmentCol = await getRawCollection<RoleAssignment>(RoleAssignment._collectionName);
  const roleCol = await getRawCollection<Role>(Role._collectionName);
  const assignment = await assignmentCol.findOne({ userId });
  const assignedRoles =
    assignment?.roleIds?.length && assignment.roleIds.length > 0
      ? await roleCol.find({ _id: { $in: assignment.roleIds } }).toArray()
      : [];

  /** NSC Admin or roles with Client.Manage see all clients; Nexus Editor/Viewer must use explicit scope rows only. */
  const unrestricted = assignedRoles.some(
    r => r.roleName === NEXUS_ADMIN_ROLE_NAME || (r.permissions ?? []).includes(NexusPermission.ClientManage),
  );

  const filter: Record<string, unknown> = !unrestricted
    ? (() => {
        const ids = getClientIdsFromScope(assignment?.scope);
        return ids.length ? { _id: { $in: ids } } : { _id: null };
      })()
    : {};

  return col.find<Client>(filter).sort({ name: 1 }).toArray();
}

export const subscribeAccessibleClients = registerSubscription({
  operationName: 'subscribeAccessibleClients',
  type: () => [Client],
  params: [],
  resolve: () => (_gqlFields: BaseQLSelectionSet<Client>) =>
    watchEntireCollection(Client._collectionName).pipe(
      debounceTime(300),
      switchMap(() => from(loadAccessibleClients())),
    ),
});
